import { action, makeObservable, observable } from 'mobx';
import { ulid } from 'ulid';
import { isSameDay, isBefore, isAfter } from 'date-fns';
import { parseFlexibleDate, parseMultiDateValue } from 'utils/date-parsing';

// --- Filter Types ---

export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than'
  | 'in_set'
  | 'date_equals'
  | 'date_before'
  | 'date_after';

export interface FieldFilter {
  id: string;
  fieldId: string;
  operator: FilterOperator;
  value: string | string[];
}

// --- Helpers ---

/** Parse multiselect cell value (JSON array or legacy comma-separated) into labels. */
function parseMultiselectCell(value: string | undefined): string[] {
  if (!value) return [];
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
      }
    } catch {
      // Fall through to legacy
    }
  }
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

// --- Operator Functions ---

type OperatorFn = (cell: string, val: any, preParsedFilterDate?: Date | null) => boolean;

const operatorFns: Record<FilterOperator, OperatorFn> = {
  equals: (cell, val) => cell === val,
  not_equals: (cell, val) => cell !== val,
  contains: (cell, val) => cell.toLowerCase().includes(String(val).toLowerCase()),
  not_contains: (cell, val) => !cell.toLowerCase().includes(String(val).toLowerCase()),
  is_empty: (cell) => !cell || cell.trim().length === 0,
  is_not_empty: (cell) => !!cell && cell.trim().length > 0,
  greater_than: (cell, val) => parseFloat(cell) > parseFloat(val),
  less_than: (cell, val) => parseFloat(cell) < parseFloat(val),
  in_set: (cell, val) => {
    // Filter value: array or comma-separated string (e.g. "150, test5")
    const arr = Array.isArray(val)
      ? val
      : typeof val === 'string' && val.trim() !== ''
      ? val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const filterValues = arr.map((v) => String(v).toLowerCase());
    // Cell value: JSON array ["150","test5"] or legacy comma-separated
    const cellValues = parseMultiselectCell(cell).map((v) => v.toLowerCase());
    return cellValues.some((cv) => filterValues.includes(cv));
  },
  date_equals: (cell, val, preParsed) => {
    const filterDate = preParsed ?? parseFlexibleDate(String(val));
    if (!filterDate) return false;
    return parseMultiDateValue(cell).some((e) => {
      const d = parseFlexibleDate(e.startDate);
      return d != null && isSameDay(d, filterDate);
    });
  },
  date_before: (cell, val, preParsed) => {
    const filterDate = preParsed ?? parseFlexibleDate(String(val));
    if (!filterDate) return false;
    return parseMultiDateValue(cell).some((e) => {
      const d = parseFlexibleDate(e.startDate);
      return d != null && isBefore(d, filterDate);
    });
  },
  date_after: (cell, val, preParsed) => {
    const filterDate = preParsed ?? parseFlexibleDate(String(val));
    if (!filterDate) return false;
    return parseMultiDateValue(cell).some((e) => {
      const d = parseFlexibleDate(e.endDate ?? e.startDate);
      return d != null && isAfter(d, filterDate);
    });
  },
};

// --- Operators by Field Type ---

export type FieldType = 'text' | 'date' | 'select' | 'multiselect' | 'number' | 'checkbox';

export const operatorsByFieldType: Record<FieldType, FilterOperator[]> = {
  text: ['equals', 'not_equals', 'contains', 'not_contains', 'is_empty', 'is_not_empty'],
  date: ['date_equals', 'date_before', 'date_after', 'is_empty', 'is_not_empty'],
  select: ['equals', 'not_equals', 'in_set', 'is_empty', 'is_not_empty'],
  multiselect: ['contains', 'not_contains', 'in_set', 'is_empty', 'is_not_empty'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'is_empty', 'is_not_empty'],
  checkbox: ['equals'],
};

export const operatorLabels: Record<FilterOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  greater_than: 'greater than',
  less_than: 'less than',
  in_set: 'is any of',
  date_equals: 'is on',
  date_before: 'is before',
  date_after: 'is after',
};

// --- Store ---

export class UiSourceFilterStore {
  filtersBySourceId: Record<string, FieldFilter[]> = {};
  filterModeBySourceId: Record<string, 'AND' | 'OR'> = {};

  constructor() {
    makeObservable(this, {
      filtersBySourceId: observable,
      filterModeBySourceId: observable,
      addFilter: action.bound,
      removeFilter: action.bound,
      updateFilter: action.bound,
      clearFilters: action.bound,
      setFilterMode: action.bound,
    });
  }

  getFilters(sourceId: string): FieldFilter[] {
    return this.filtersBySourceId[sourceId] || [];
  }

  getFilterMode(sourceId: string): 'AND' | 'OR' {
    return this.filterModeBySourceId[sourceId] || 'AND';
  }

  hasFilters(sourceId: string): boolean {
    return (this.filtersBySourceId[sourceId]?.length || 0) > 0;
  }

  hasFilterOnField(sourceId: string, fieldId: string): boolean {
    return this.getFilters(sourceId).some((f) => f.fieldId === fieldId);
  }

  addFilter(
    sourceId: string,
    fieldId: string,
    operator: FilterOperator,
    value: string | string[] = '',
  ) {
    if (!this.filtersBySourceId[sourceId]) {
      this.filtersBySourceId[sourceId] = [];
    }
    this.filtersBySourceId[sourceId].push({
      id: ulid(),
      fieldId,
      operator,
      value,
    });
  }

  removeFilter(sourceId: string, filterId: string) {
    const filters = this.filtersBySourceId[sourceId];
    if (!filters) return;
    this.filtersBySourceId[sourceId] = filters.filter((f) => f.id !== filterId);
  }

  updateFilter(
    sourceId: string,
    filterId: string,
    updates: Partial<Pick<FieldFilter, 'fieldId' | 'operator' | 'value'>>,
  ) {
    const filters = this.filtersBySourceId[sourceId];
    if (!filters) return;
    const filter = filters.find((f) => f.id === filterId);
    if (!filter) return;
    if (updates.fieldId !== undefined) filter.fieldId = updates.fieldId;
    if (updates.operator !== undefined) filter.operator = updates.operator;
    if (updates.value !== undefined) filter.value = updates.value;
  }

  clearFilters(sourceId: string) {
    this.filtersBySourceId[sourceId] = [];
  }

  setFilterMode(sourceId: string, mode: 'AND' | 'OR') {
    this.filterModeBySourceId[sourceId] = mode;
  }

  // Core evaluation: does a row match all/any active filters?
  // validFieldIds: skip orphaned filters (e.g. when column was deleted)
  matchesRow(
    sourceId: string,
    getValue: (fieldId: string) => string | undefined,
    validFieldIds?: Set<string>,
  ): boolean {
    let filters = this.getFilters(sourceId);
    if (validFieldIds) {
      filters = filters.filter((f) => validFieldIds.has(f.fieldId));
    }
    if (filters.length === 0) return true;

    const mode = this.getFilterMode(sourceId);
    const method = mode === 'AND' ? 'every' : 'some';

    // Cache parsed date filter values (avoids N×parseFlexibleDate per filteredRows computation)
    const dateFilterCache = new Map<string, Date | null>();
    for (const f of filters) {
      if (['date_equals', 'date_before', 'date_after'].includes(f.operator)) {
        dateFilterCache.set(f.id, parseFlexibleDate(String(f.value)) ?? null);
      }
    }

    return filters[method]((filter) => {
      const cellValue = getValue(filter.fieldId) || '';
      const fn = operatorFns[filter.operator];
      const preParsed = dateFilterCache.get(filter.id);
      return fn ? fn(cellValue, filter.value, preParsed) : true;
    });
  }
}
